import React, { useState, useRef, useEffect, Fragment, useLayoutEffect } from 'react';
import '../css/Report.css'; // Import the custom CSS file
import '../css/BindList.css'; // Import the custom CSS file
import {
  getColumnClassName,
  getColumnColor,
  getHeaderColor,
} from './ReportStyle.js';
import '../css/FilesTable.css'; // Import the custom CSS file

// Icon imports
import { LuRefreshCw } from "react-icons/lu";
import { MdOutlineFileDownloadDone } from "react-icons/md";
import { AllServerFilesObject } from '../App';

// Define the structure of the object in the detector data
interface DetectorObject {
  mac: string;
  ip: string;
  last_active: string;
  online: boolean;
  status: {
    site_name: string;
    gps_fix: boolean;
    gps_lat: number;
    gps_long: number;
    up_since: string;
    memory: string;
    fw_version: string;
    test_mode: boolean;
    thr: string;
    linux: boolean;
    sim_card_no?: string;
  };
}

interface ReportComponentProps {
  detectorData: DetectorObject[];
  fileData: AllServerFilesObject | null;
  fileDataLastModified: string | null;
  reportGenAt: string;
  preferredColumnsOrder: any;
  expandedRow: any;
  setExpandedRow: any;
  onRefreshFileData: () => Promise<void>;
  onFilePathSelect?: (filePath: string) => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

const getColorForMemory = (count: number, isDetectorOn: boolean) => {
  if (!isDetectorOn) return
  if (count > 15) return "red"; //red
  if (count > 0.5 && count <= 15) return "#d5b60a"; //yellow
  if (count <= 0.5) return;
};

const getColorForGPSFix = (isFixed: boolean, isDetectorOn: boolean) => {
  if (!isDetectorOn) return
  if (isFixed === false) return "red"; //red
  if (isFixed === true) return;
};

const calculateTimeAgo = (lastActive: string): string => {
  // Parse the string into a Date object in UTC
  const lastActiveDate = new Date(lastActive + 'Z'); // Ensure it's treated as UTC

  // Get the current date and time in UTC
  const now = new Date(Date.now()); // Get the current date and time
  // Calculate the difference in seconds
  const differenceInSeconds = Math.floor(
    (now.getTime() - lastActiveDate.getTime()) / 1000
  );

  // Calculate time in different units
  const minutes = Math.floor(differenceInSeconds / 60);
  const hours = Math.floor(differenceInSeconds / 3600);
  const days = Math.floor(differenceInSeconds / 86400);

  // Format the output
  if (differenceInSeconds < 60) {
    return `${differenceInSeconds} second${differenceInSeconds !== 1 ? 's' : ''} ago`;
  } else if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
};

const ReportComponent: React.FC<ReportComponentProps> = ({
  detectorData,
  fileData,
  fileDataLastModified,
  reportGenAt,
  preferredColumnsOrder,
  expandedRow,
  setExpandedRow,
  onRefreshFileData,
  onFilePathSelect  // <== Add this here
}) => {
  const filterRef = useRef<'all' | 'online' | 'offline'>('all');
  const [filter, setFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [sortOrder, setSortOrder] = useState<'alphabetical' | 'newest' | 'oldest'>('alphabetical');
  const [isExpanded, setIsExpanded] = useState(false); // state to handle expansion

  // Controlling the state of the file generation
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const fileListRef = useRef<HTMLUListElement>(null);
  const scrollYRef = useRef<number>(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const handleRefreshClick = async () => {
    if (isRefreshing) return; // prevent double-clicks

    setIsRefreshing(true);
    setIsDone(false);
    try {
      await onRefreshFileData(); // wait for the async task
      setIsRefreshing(false);
      setIsDone(true);
      setTimeout(() => {
        setIsDone(false);
      }, 5000); // show success icon for 5 seconds
    } catch (error) {
      console.error("Refresh failed:", error);
      setIsRefreshing(false);
    }
  };

  const toggleExpansion = () => {
    setIsExpanded((prevState) => !prevState); // toggle the expanded state
    localStorage.setItem('scrollPosition', "0"); // Store in localStorage
  };

  // Handle select dropdown change
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedFilter = event.target.value as 'all' | 'online' | 'offline';
    filterRef.current = selectedFilter;
    setFilter(selectedFilter);
    localStorage.setItem('lastFilter', selectedFilter); // <-- Save to localStorage
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSort = event.target.value as 'alphabetical' | 'newest' | 'oldest';
    setSortOrder(selectedSort);
    localStorage.setItem('lastSort', selectedSort); // <-- Save to localStorage
  };

  useEffect(() => {
    const savedFilter = localStorage.getItem('lastFilter') as 'all' | 'online' | 'offline';
    const savedSort = localStorage.getItem('lastSort') as 'alphabetical' | 'newest' | 'oldest';

    if (savedFilter) {
      filterRef.current = savedFilter;
      setFilter(savedFilter);
    }

    if (savedSort) {
      setSortOrder(savedSort);
    }
  }, []);




  // Convert object to array of values if needed
  const detectorArray: DetectorObject[] = Array.isArray(detectorData)
    ? detectorData
    : Object.values(detectorData);

  // Filter based on useRef value
  const filteredData =
    filterRef.current === 'all'
      ? detectorArray
      : detectorArray.filter((obj) => obj.online === (filterRef.current === 'online'));

  const sortedData = [...filteredData];

  if (sortOrder === 'alphabetical') {
    sortedData.sort((a, b) => {
      const nameA = a.status.site_name || 'zzz';
      const nameB = b.status.site_name || 'zzz';
      return nameA.localeCompare(nameB);
    });
  } else if (sortOrder === 'newest') {
    sortedData.reverse();
  }
  else if (sortOrder === 'oldest') {
    sortedData;
  }


  const ObjectTable: React.FC<{ data: DetectorObject[] }> = ({ data }) => {
    const toggleRow = (mac: string) => {
      setIsExpanded(false)

      localStorage.setItem('scrollPosition', "0"); // Store in localStorage

      if (expandedRow === mac) {
        setExpandedRow(null);
      } else {
        setExpandedRow(mac);
      }
    };

    // Save scroll position to localStorage when scrolling
    useLayoutEffect(() => {
      const list = fileListRef.current;
      if (!list) return;

      const saveScroll = () => {
        if (list.scrollTop > 0) {  // Prevent saving scrollTop as 0 on page load
          scrollYRef.current = list.scrollTop;
          // console.log('✅ Saved scrollTop:', scrollYRef.current);
          localStorage.setItem('scrollPosition', scrollYRef.current.toString()); // Store in localStorage
        }
      };

      list.addEventListener('scroll', saveScroll);

      return () => {
        list.removeEventListener('scroll', saveScroll);
      };
    }, []);

    // Restore scroll position on component mount
    useLayoutEffect(() => {
      const savedScroll = localStorage.getItem('scrollPosition');
      if (savedScroll && fileListRef.current) {
        const scrollPosition = parseFloat(savedScroll);
        if (!isNaN(scrollPosition)) {
          fileListRef.current.scrollTop = scrollPosition;
        }
      }

      // Tell the component scroll restoration is complete
      setIsDataLoaded(true);
    }, [fileData]);


    return (
      <div className="table-responsive-xl m-4">
        <table className="backend table table-hover table-dark">
          <thead className='table-header'>
            <tr className='table-tr'>
              <th>Site Name</th>
              <th>MAC</th>
              <th>IP</th>
              <th>Last Active</th>
              <th>Online</th>
              <th>Memory</th>
              <th>GPS Fix</th>
            </tr>
          </thead>
          <tbody className="expanded-table-whole">
            {data.map((obj, index) => (
              <Fragment key={obj.mac}>
                <tr
                  onClick={() => toggleRow(obj.mac)}
                  style={{ cursor: 'pointer' }}
                  className={getColumnColor(obj.online)}
                >
                  <td>{obj.status.site_name}</td>
                  <td>{obj.mac}</td>
                  <td>{obj.ip}</td>
                  <td>{calculateTimeAgo(obj.last_active)}</td>
                  <td>{obj.online ? 'Yes' : 'No'}</td>
                  <td>
                    <p
                      style={{
                        color: getColorForMemory(Number(obj.status.memory), obj.online),
                        display: 'inline',
                        margin: 0,
                      }}
                    >
                      {obj.status.memory}
                    </p>
                  </td>
                  <td>
                    <p
                      style={{
                        color: getColorForGPSFix(obj.status.gps_fix, obj.online),
                        display: 'inline',
                        margin: 0,
                      }}
                    >
                      {obj.status.gps_fix ? 'Yes' : 'No'}
                    </p>
                  </td>
                </tr>
                {expandedRow === obj.mac && (
                  <tr>
                    <td colSpan={7}>
                      <div className="p-2">
                        <strong>Status Details:</strong>
                        <table className="backend table table-hover table-dark">
                          <tbody className="expanded-table">
                            {/* Expanded table rows */}
                            <tr>
                              <td>GPS Fix</td>
                              <td>
                                <p style={{
                                  color: getColorForGPSFix(obj.status.gps_fix, obj.online),
                                  display: 'inline',
                                  margin: 0
                                }}>
                                  {obj.status.gps_fix ? 'Yes' : 'No'}
                                </p>
                              </td>
                            </tr>
                            <tr
                              className="cursor-pointer hover:bg-gray-100"
                              onClick={() =>
                                window.open(
                                  `https://www.google.com/maps/search/?api=1&query=${obj.status.gps_lat},${obj.status.gps_long}`,
                                  "_blank",
                                  "noopener,noreferrer"
                                )
                              }
                            >
                              <td>Location</td>
                              <td>{obj.status.gps_lat}, {obj.status.gps_long}</td>
                            </tr>
                            <tr><td>Last Seen</td><td>{formatDate(obj.last_active)} UTC</td></tr>
                            <tr><td>Up Since</td><td>{obj.status.up_since}</td></tr>
                            <tr><td>Site Name</td><td>{obj.status.site_name}</td></tr>
                            <tr><td>Memory</td>
                              <td>
                                <p style={{
                                  color: getColorForMemory(Number(obj.status.memory), obj.online),
                                  display: 'inline',
                                  margin: 0
                                }}>
                                  {obj.status.memory}
                                </p>
                              </td>
                            </tr>
                            <tr><td>Firmware Version</td><td>{obj.status.fw_version}</td></tr>
                            <tr><td>Test Mode</td><td>{obj.status.test_mode ? 'Yes' : 'No'}</td></tr>
                            <tr><td>Threshold</td><td>{obj.status.thr}</td></tr>
                            <tr><td>Linux</td><td>{obj.status.linux ? 'Yes' : 'No'}</td></tr>
                            <tr>
                              {obj.status.sim_card_no && obj.status.sim_card_no !== "N/A" ? (
                                <>
                                  <td>Sim Card</td>
                                  <td>{obj.status.sim_card_no}</td>
                                </>
                              ) : null}
                            </tr>
                            <tr>
                              <td className='download-button' colSpan={2} onClick={toggleExpansion}>Most Recent 100 files</td>
                            </tr>


                            {isExpanded && (
                              <tr className='file-tr' >
                                <td colSpan={2}>
                                  {fileData ? (() => {
                                    const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '');
                                    const matchingEntry = Object.entries(fileData).find(
                                      ([siteName]) => normalize(siteName) === normalize(obj.status.site_name)
                                    );

                                    if (matchingEntry && matchingEntry[1].length > 0 && isDataLoaded) {
                                      return (
                                        <div>
                                          <div className='file-header'>
                                            <div className='file-name-header'>Filename</div>
                                            <div className='file-timestamp'>Timestamp</div>
                                          </div>
                                          <ul ref={fileListRef} className='file-list'>
                                            {matchingEntry[1].map((f, i) => {
                                              const isBinFile = f.filename.includes("bin");

                                              const handleClick = () => {
                                                if (isBinFile) {
                                                  // console.log(f.filepath); // Only log for bin files
                                                  if (onFilePathSelect) {
                                                    onFilePathSelect(f.filepath);
                                                  }
                                                }

                                              };

                                              return (
                                                <li
                                                  key={i}
                                                  className={isBinFile ? 'file-list-item' : 'file-list-item2'}
                                                  onClick={isBinFile ? handleClick : undefined}
                                                  style={{ cursor: isBinFile ? 'pointer' : 'default' }}
                                                >
                                                  <div className='file-name'>{f.filename}</div>
                                                  <div className='file-timestamp'>{f.timestamp}</div>
                                                </li>
                                              );
                                            })}
                                          </ul>


                                        </div>
                                      );
                                    } else {
                                      return <div className='no-results-display'>No files found</div>;
                                    }

                                  })() : <span>No files found</span>}
                                </td>
                              </tr>
                            )}


                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}

              </Fragment>
            ))}
          </tbody>
        </table >
      </div >
    );
  };

  return (
    <div className='report-wrapper'>
      {reportGenAt && (
        <Fragment>
          <div className="headerPlusFilter m-3">


            <div className='report-filter-options'>

              <h3 className={getHeaderColor(reportGenAt)}>
                Report Generated at: {formatDate(reportGenAt)}
              </h3>

              <label className="on-off-filter">
                Status:
                <select
                  id="filterSelect"
                  className="on-off-select"
                  value={filter}
                  onChange={handleSelectChange}
                >
                  <option value="all">On | Off</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </label>

              <label className="on-off-filter">
                Sort:
                <select
                  id="sortSelect"
                  className="on-off-select"
                  value={sortOrder}
                  onChange={handleSortChange}
                >
                  <option value="alphabetical">Alphabetical</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </label>
            </div>

            <div className='generated-info'>
              {fileDataLastModified && (
                <h3>
                  Files Generated at: {formatDate(fileDataLastModified)}
                </h3>
              )}


              <label className="on-off-filter">
                Files
                {isDone ? (
                  <MdOutlineFileDownloadDone className="refresh-button success" />
                ) : (
                  <LuRefreshCw
                    className={`refresh-button ${isRefreshing ? 'rotating' : ''}`}
                    onClick={handleRefreshClick}
                  />
                )}
              </label>
            </div>


          </div>
        </Fragment>
      )}

      <ObjectTable data={sortedData} />

    </div>
  );

};

export default ReportComponent;